import { CBForm } from '@cb/apricot'
import { NotiType, ProgressBar, addNotification, notificationsState } from '@landscape/ui-lib'
import { setModalOptions } from '@landscape/ui-redux'
import { getClient } from 'apollo/configuration'
import { uploadFileToS3 } from 'contextUtil'
import { find, isEmpty, matchesProperty } from 'lodash'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'

const StyledButton = styled.button`
  background-color: #0177c8;
  color: #fff;
  border-color: #0177c8;
  font-size: 1rem;
  &:hover {
    background-color: #fedb00;
    color: #1e1e1e;
    transform: scale(1.01);
    transform: translateY(-2px);
  }
  &:disabled {
    background-color: initial;
    color: initial;
    transform: initial;
    transform: initial;
  }
`

const FileUploader: React.FC<any> = (props: any) => {
  // used to dispatch actions to the redux store, triggering state change
  const dispatch = useDispatch()
  const setNotis = useSetRecoilState(notificationsState)
  let { disableButton = false } = props
  const [isButtonDisabled, setIsButtonDisabled] = useState(disableButton)

  const [percentage, setPercentage] = useState(0)
  const inputRef = useRef(null)

  useEffect(() => {
    CBForm.fileUpload({
      elem: inputRef.current,
      fdbk: false,
      markup: false,
    })
  }, [])

  const onSuccess = useCallback((file) => {
    const uploadSuccessMessage = `File uploading was successful and is now being processed. A notification message will appear once processing is complete.`

    const notiProps = {
      type: NotiType.INFO,
      message: uploadSuccessMessage,
      uniqueId: file.name.split('.')[0],
    }

    addNotification(notiProps, setNotis)

    setIsButtonDisabled(true)
    setTimeout(() => {
      setIsButtonDisabled(false)
    }, 5000)
  }, [])

  const handleFileUpload = (event: any) => {
    const target = event.target
    const file = target.files[0]
    // FIRST check that the file is REALLY a csv
    if (file && file.name && file.name.slice(-4).toLowerCase() !== '.csv') {
      const nonCsvErrorMsg = [
        'The following errors have prevented acceptance of your Landscape data file. Please correct and resubmit.\n\n',
        'Error: File not in CSV format.',
      ]

      dispatch(
        setModalOptions({
          showModal: true,
          body: nonCsvErrorMsg,
          status: 'danger',
          title: 'Critical File Errors',
          primaryLabel: 'OK',
          confirm: false,
          contactInfo: true,
        })
      )
      return
    }

    uploadFileToS3(file.name, file, setPercentage, dispatch, getClient().orgId)
      .then((validationErrors: any) => {
        if (isEmpty(validationErrors)) {
          onSuccess(file)
        } else if (isEmpty(find(validationErrors, matchesProperty('kind', 'fatal')))) {
          /* Validation Path: Warnings */
          onSuccess(file)
        } else {
          /* Validation Path: Fatal Errors */
          const displayErrors: any = []
          let fatalErrorMessageIndex = 1

          const fatalErrors = validationErrors.filter((error) => {
            return error.kind === 'fatal'
          })

          fatalErrors.forEach((error) => {
            displayErrors.push(`Error ${fatalErrorMessageIndex}: ${error.message} \n`)
            ++fatalErrorMessageIndex
          })
          dispatch(
            setModalOptions({
              showModal: true,
              body: displayErrors,
              status: 'danger',
              title: 'Critical File Errors',
              primaryLabel: 'OK',
              confirm: false,
              contactInfo: true,
            })
          )
        }
      })
      .catch((error) => {
        dispatch(
          setModalOptions({
            showModal: true,
            body: error.message,
            status: 'danger',
            title: 'Critical Errors',
            primaryLabel: 'OK',
            confirm: false,
            contactInfo: true,
          })
        )
      })
      .finally(() => {
        setPercentage(0)
      })
  }

  return (
    <>
      {/* Progress bar with 400px width */}
      <ProgressBar width={400} percent={percentage} />

      <form encType="multipart/form-data">
        <br />
        <div className="cb-file-upload display-flex flex-column justify-content-center align-items-center">
          <label
            id="csvInputFile"
            htmlFor="fileBtnfdbk"
            className="cb-margin-bottom-16 cb-font-weight-bold"
          >
            Choose one or more CSV files to upload
          </label>
          <input
            name="fileUpload"
            type="file"
            id="fileBtnfdbk"
            data-testid="file-upload-input"
            onChange={handleFileUpload}
            accept=".csv"
            onClick={(event: any) => {
              event.target.value = ''
            }}
            ref={inputRef}
            disabled={isButtonDisabled}
          />
          <StyledButton
            type="button"
            disabled={isButtonDisabled}
            aria-describedby="csvInputFile"
            className={`col-xs-6 col-md-3 cb-file-button cb-btn-primary cb-btn cb-btn-lg cb-btn-yellow ${
              isButtonDisabled ? 'cb-btn-pulse cb-disabled' : ''
            }`}
          >
            Browse...
            <div className="cb-btn-pulse-dots">
              <div className="cb-btn-dots"></div>
            </div>
          </StyledButton>
          <span className="cb-file-element"></span>
        </div>
      </form>
    </>
  )
}

export { FileUploader }
